import React, { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';

// Standard template categories
const DEFAULT_CATEGORIES = [
  'Promotional',
  'Transactional',
  'Reminder',
  'Announcement',
  'General'
];

interface TemplateCategoryFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  restaurantId?: string; // Add restaurantId prop to fetch unique categories
}

const TemplateCategoryField: React.FC<TemplateCategoryFieldProps> = ({
  value,
  onChange,
  disabled = false,
  restaurantId
}) => {
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // Fetch unique categories from existing templates
  useEffect(() => {
    const fetchUniqueCategories = async () => {
      if (!restaurantId) return;
      
      setIsLoadingCategories(true);
      try {
        console.log(`Fetching template categories for restaurant: ${restaurantId}`);
        
        // First try to get unique categories using Supabase
        try {
          // Dynamic import to avoid SSR issues
          const { supabase } = await import('@/integrations/supabase/client');
          
          // First, get the organisation_id for this restaurant
          const { data: restaurantData } = await supabase
            .from('restaurants_master')
            .select('organisation_id')
            .eq('restaurant_id', restaurantId)
            .maybeSingle();
          
          const organisationId = restaurantData?.organisation_id;
          
          // Get categories from database
          let query = supabase
            .from('sms_templates')
            .select('category');
          
          if (organisationId) {
            // Get both restaurant and organisation template categories
            query = query.or(`restaurant_id.eq.${restaurantId},organisation_id.eq.${organisationId}`);
          } else {
            // Just get restaurant template categories
            query = query.eq('restaurant_id', restaurantId);
          }
          
          const { data, error } = await query;
          
          if (error) {
            console.error('Error fetching categories from database:', error);
          } else if (data && data.length > 0) {
            // Extract unique categories that are not null or empty
            const dbCategories = data
              .map(item => item.category)
              .filter(category => category && category.trim() !== '')
              .map(category => category.trim());
            
            console.log('Categories found in database:', dbCategories);
            
            if (dbCategories.length > 0) {
              // Use Set to remove duplicates
              const uniqueCategories = [...new Set([...DEFAULT_CATEGORIES, ...dbCategories])];
              console.log('Using categories from database:', uniqueCategories);
              setCategories(uniqueCategories);
              setIsLoadingCategories(false);
              return;
            }
          }
        } catch (dbError) {
          console.error('Error in direct database query for categories:', dbError);
          // Continue to API fallback
        }
        
        // Fallback to API if database query fails
        console.log('Falling back to API for template categories');
        
        // Import the fetchTemplates function
        const { fetchTemplates } = await import('@/services/smsApi');
        
        // Fetch templates for this restaurant
        const templatesResult = await fetchTemplates(restaurantId);
        
        // Extract unique categories from templates
        let templateCategories: string[] = [];
        
        // Handle different response formats
        if (templatesResult && Array.isArray(templatesResult)) {
          // Direct array format
          templateCategories = templatesResult
            .map(template => template.category || 'General')
            .filter(Boolean);
        } else if (templatesResult && templatesResult.templates && Array.isArray(templatesResult.templates)) {
          // Object with templates array format
          templateCategories = templatesResult.templates
            .map(template => template.category || 'General')
            .filter(Boolean);
        }
        
        console.log('Template categories from API:', templateCategories);
        
        // Combine with default categories and remove duplicates
        const uniqueCategories = [...new Set([...DEFAULT_CATEGORIES, ...templateCategories])];
        
        console.log('Final unique categories:', uniqueCategories);
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching template categories:', error);
        // Fall back to default categories
      } finally {
        setIsLoadingCategories(false);
      }
    };
    
    fetchUniqueCategories();
  }, [restaurantId]);

  // Add a new custom category
  const handleAddCategory = () => {
    if (newCategoryName.trim() === '') return;
    
    const formattedCategory = newCategoryName.trim();
    // Only add if it doesn't already exist (case insensitive)
    if (!categories.some(cat => cat.toLowerCase() === formattedCategory.toLowerCase())) {
      const updatedCategories = [...categories, formattedCategory];
      setCategories(updatedCategories);
      // Select the new category
      onChange(formattedCategory);
    }
    
    // Reset the form
    setNewCategoryName('');
    setIsAddingNewCategory(false);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="template-category">Category</Label>
      
      {!isAddingNewCategory ? (
        <div className="flex gap-2">
          <Select
            value={value}
            onValueChange={onChange}
            disabled={disabled || isLoadingCategories}
          >
            <SelectTrigger id="template-category" className="flex-1">
              <SelectValue placeholder={isLoadingCategories ? "Loading categories..." : "Select a category"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Available Categories</SelectLabel>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setIsAddingNewCategory(true)}
            disabled={disabled}
            title="Add custom category"
          >
            <PlusCircle className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            id="new-category"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Enter new category"
            className="flex-1"
            autoFocus
          />
          <div className="flex gap-1">
            <Button 
              type="button" 
              variant="default" 
              size="sm"
              onClick={handleAddCategory}
              disabled={newCategoryName.trim() === ''}
            >
              Add
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={() => {
                setNewCategoryName('');
                setIsAddingNewCategory(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground">
        Templates are grouped by category for easier management
      </p>
    </div>
  );
};

export default TemplateCategoryField;